import { useQuery } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import chatAccessErrorCode from "./chatAccessErrorCode";
import { getChatAccess } from "./getChatAccess";

export const CHAT_ACCESS_QUERY_KEY = ["cc-chain-api", "access"];

/**
 * - `allowed`: the user can chat.
 * - `warned`: not in the group, but access isn't enforced yet.
 * - `denied`: not in the group, and access is enforced.
 * - `unavailable`: the API couldn't check membership.
 * - `unknown`: still loading, or the check failed. Treated as `allowed`.
 */
export type ChatAccess =
  | "allowed"
  | "warned"
  | "denied"
  | "unavailable"
  | "unknown";

interface ChatAccessResult {
  access: ChatAccess;
  enforced: boolean;
}

function useChatAccessQuery() {
  const getAccessToken = useGetAccessToken();

  return useQuery({
    queryKey: CHAT_ACCESS_QUERY_KEY,
    queryFn: async (): Promise<ChatAccessResult> => {
      try {
        const { enforced, member } = await getChatAccess(getAccessToken);
        const access = !enforced && member === false ? "warned" : "allowed";
        return { access, enforced };
      } catch (error) {
        const code = await chatAccessErrorCode(error);
        if (code === "group_required") {
          return { access: "denied", enforced: true };
        }
        if (code === "group_check_unavailable") {
          return { access: "unavailable", enforced: true };
        }
        return { access: "unknown", enforced: false };
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The signed-in user's chat access, from `GET /chat/access` (ADR 076). */
export default function useChatAccess(): ChatAccess {
  return useChatAccessQuery().data?.access ?? "unknown";
}

/** True when the user is blocked from chat. */
export function isChatBlocked(access: ChatAccess): boolean {
  return access === "denied" || access === "unavailable";
}
